import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  SalesExample,
  CreateExampleData,
  SalesExampleCategory,
  CATEGORY_LABELS,
} from '../types/sales-training.types';

interface ExampleFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateExampleData) => Promise<void>;
  example?: SalesExample | null;
}

const CATEGORIES: SalesExampleCategory[] = [
  'OBJECTION',
  'GREETING',
  'CLOSING',
  'PRICING',
  'GENERAL',
];

export function ExampleFormDialog({ open, onClose, onSubmit, example }: ExampleFormDialogProps) {
  const [category, setCategory] = useState<SalesExampleCategory>('GENERAL');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [context, setContext] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (example) {
      setCategory(example.category);
      setQuestion(example.question);
      setAnswer(example.answer);
      setContext(example.context || '');
    } else {
      setCategory('GENERAL');
      setQuestion('');
      setAnswer('');
      setContext('');
    }
  }, [example, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || !answer.trim()) return;

    setSaving(true);
    try {
      await onSubmit({
        category,
        question: question.trim(),
        answer: answer.trim(),
        context: context.trim() || null,
      });
      onClose();
    } catch {
      // error handled by store
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{example ? 'Editar Ejemplo' : 'Nuevo Ejemplo de Respuesta'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category">Categoría</Label>
            <SearchableSelect
              value={category}
              onValueChange={(val) => setCategory(val as SalesExampleCategory)}
              options={CATEGORIES.map((cat) => ({ value: cat, label: CATEGORY_LABELS[cat] }))}
              placeholder="Seleccionar categoría..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="question">Pregunta / Situación del cliente</Label>
            <textarea
              id="question"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder='Ej: "El precio es muy alto comparado con la competencia"'
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="answer">Respuesta modelo del agente</Label>
            <textarea
              id="answer"
              className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Como debe responder el agente ante esta situación..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="context">Contexto (opcional)</Label>
            <Input
              id="context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Ej: Cliente enterprise, negociación de volumen"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || !question.trim() || !answer.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {example ? 'Guardar Cambios' : 'Crear Ejemplo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ExampleFormDialog;
